import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import Player from "@vimeo/player";
import { createTrimPreview } from "../../lib/trimPreview";
import { createVimeoTrimTransport } from "../../lib/vimeoTrimTransport";

export type TrimPlayerHandle = {
  currentTime(): Promise<number>;
  seek(seconds: number, play?: boolean): Promise<void>;
  preview(start: number, end: number): Promise<void>;
  stopPreview(): void;
};

export const RoutineTrimPlayer = forwardRef<
  TrimPlayerHandle,
  {
    url: string;
    embedded: boolean;
    title: string;
    onDuration: (seconds: number) => void;
    onError: (message: string) => void;
    onTime?: (seconds: number) => void;
  }
>(function RoutineTrimPlayer(
  { url, embedded, title, onDuration, onError, onTime },
  ref,
) {
  const native = useRef<HTMLVideoElement>(null);
  const iframe = useRef<HTMLIFrameElement>(null);
  const vimeo = useRef<Player | null>(null);
  const preview = useRef<ReturnType<typeof createTrimPreview> | null>(null);
  const callbacks = useRef({ onDuration, onError, onTime });
  callbacks.current = { onDuration, onError, onTime };
  useEffect(() => {
    const element = native.current;
    const player =
      embedded && iframe.current ? new Player(iframe.current) : null;
    vimeo.current = player;
    if (!player && !element) return;
    let loadedDuration = 0;
    const transport = player ? createVimeoTrimTransport(player, () => loadedDuration) : null;
    const controller = createTrimPreview(
      transport ?? {
            currentTime: async () => element!.currentTime,
            seek: async (seconds) => {
              element!.currentTime = seconds;
            },
            play: () => element!.play(),
            pause: async () => {
              element!.pause();
            },
          },
      () =>
        callbacks.current.onError(
          "The preview could not be stopped reliably. Pause the video and try Preview selected portion again.",
        ),
      (seconds) => callbacks.current.onTime?.(seconds),
    );
    preview.current = controller;
    const cleanup = () => {
      controller.dispose();
      transport?.dispose();
      preview.current = null;
      vimeo.current = null;
    };
    if (!player) return cleanup;
    let alive = true;
    const fail = () => {
      if (alive)
        callbacks.current.onError(
          "The performance video could not load. Check its video link and Vimeo playback access.",
        );
    };
    const timer = window.setTimeout(fail, 20000);
    void player
      .ready()
      .then(() => player.getDuration())
      .then((seconds) => {
        window.clearTimeout(timer);
        loadedDuration = seconds;
        if (alive && seconds > 0) callbacks.current.onDuration(seconds);
      })
      .catch(fail);
    const timeUpdate = () => controller.check();
    player.on("timeupdate", timeUpdate);
    player.on("seeked", timeUpdate);
    player.on("pause", timeUpdate);
    player.on("error", fail);
    return () => {
      alive = false;
      window.clearTimeout(timer);
      player.off("timeupdate", timeUpdate);
      player.off("seeked", timeUpdate);
      player.off("pause", timeUpdate);
      player.off("error", fail);
      cleanup();
      // React owns the iframe; destroying the SDK would remove its DOM node.
    };
  }, [url, embedded]);
  useImperativeHandle(
    ref,
    () => ({
      currentTime: async () =>
        embedded
          ? ((await vimeo.current?.getCurrentTime()) ?? 0)
          : (native.current?.currentTime ?? 0),
      stopPreview: () => {
        void preview.current?.stop();
      },
      seek: async (seconds, play = false) => {
        if (!preview.current) return;
        await preview.current.seek(seconds, play);
      },
      preview: async (start, end) => {
        if (!preview.current)
          throw new Error("The performance video is still loading.");
        await preview.current.preview(start, end);
      },
    }),
    [embedded],
  );
  return embedded ? (
    <iframe
      ref={iframe}
      src={url}
      title={title}
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      className="aspect-video w-full rounded-xl border-0 bg-black"
    />
  ) : (
    <video
      ref={native}
      src={url}
      controls
      playsInline
      preload="metadata"
      className="aspect-video w-full rounded-xl bg-black"
      onLoadedMetadata={() =>
        callbacks.current.onDuration(native.current!.duration)
      }
      onTimeUpdate={() => {
        preview.current?.check();
      }}
      onSeeked={() => preview.current?.check()}
      onPause={() => preview.current?.check()}
      onError={() =>
        callbacks.current.onError(
          "The performance video could not load. Check its video link and playback access.",
        )
      }
    />
  );
});
